import { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getCoshhData, GetCoshhDataOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FlaskConical, Search, ChevronDown, ChevronUp, ExternalLink, ShieldAlert, HandMetal, Droplets, Package, AlertTriangle } from 'lucide-react';

type Substance = GetCoshhDataOutputType['substances'][0];

const CATEGORIES = ['Cleaning', 'Kitchen', 'Cellar', 'Maintenance', 'Pest Control', 'Laundry', 'Chemicals', 'Other'];
const RISK_CONFIG = {
  Low: { cls: 'bg-green-500/10 text-green-700 border-green-500/30', bar: 'bg-green-500', width: 'w-1/3' },
  Medium: { cls: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/30', bar: 'bg-yellow-500', width: 'w-2/3' },
  High: { cls: 'bg-red-500/10 text-red-700 border-red-500/30', bar: 'bg-red-500', width: 'w-full' },
};

function SubstanceCard({ sub }: { sub: Substance }) {
  const [expanded, setExpanded] = useState(false);
  const risk = RISK_CONFIG[sub.riskLevel as keyof typeof RISK_CONFIG];

  return (
    <div className="border border-border bg-card rounded-xl overflow-hidden">
      <button className="w-full text-left p-4 hover:bg-muted/20 transition-colors" onClick={() => setExpanded(e => !e)}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="font-semibold text-foreground">{sub.productName}</span>
              {sub.riskLevel && <Badge variant="outline" className={`text-xs ${risk?.cls || ''}`}>{sub.riskLevel} Risk</Badge>}
              {sub.category && <span className="text-xs bg-muted text-muted-foreground rounded px-1.5 py-0.5">{sub.category}</span>}
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {sub.locationUsed && <span>📍 {sub.locationUsed}</span>}
              {sub.supplier && <span>Supplier: {sub.supplier}</span>}
            </div>
            {risk && (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-muted-foreground">Risk:</span>
                <div className="flex-1 bg-muted rounded-full h-1.5 max-w-[80px]">
                  <div className={`h-1.5 rounded-full ${risk.bar} ${risk.width}`} />
                </div>
              </div>
            )}
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border bg-muted/10 p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {sub.hazardsDescription && (
              <div>
                <div className="flex items-center gap-1.5 mb-1.5"><ShieldAlert className="w-3.5 h-3.5 text-red-500" /><span className="text-xs font-bold text-foreground uppercase tracking-wider">Hazards</span></div>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{sub.hazardsDescription}</p>
              </div>
            )}
            {sub.ppeRequired && (
              <div>
                <div className="flex items-center gap-1.5 mb-1.5"><HandMetal className="w-3.5 h-3.5 text-blue-500" /><span className="text-xs font-bold text-foreground uppercase tracking-wider">PPE Required</span></div>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{sub.ppeRequired}</p>
              </div>
            )}
            {sub.firstAidMeasures && (
              <div>
                <div className="flex items-center gap-1.5 mb-1.5"><AlertTriangle className="w-3.5 h-3.5 text-orange-500" /><span className="text-xs font-bold text-foreground uppercase tracking-wider">First Aid</span></div>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{sub.firstAidMeasures}</p>
              </div>
            )}
            {sub.storageInstructions && (
              <div>
                <div className="flex items-center gap-1.5 mb-1.5"><Package className="w-3.5 h-3.5 text-purple-500" /><span className="text-xs font-bold text-foreground uppercase tracking-wider">Storage</span></div>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{sub.storageInstructions}</p>
              </div>
            )}
            {sub.spillProcedure && (
              <div className="sm:col-span-2">
                <div className="flex items-center gap-1.5 mb-1.5"><Droplets className="w-3.5 h-3.5 text-cyan-500" /><span className="text-xs font-bold text-foreground uppercase tracking-wider">Spill Procedure</span></div>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{sub.spillProcedure}</p>
              </div>
            )}
          </div>
          {sub.sdsLink && (
            <a href={sub.sdsLink} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline border border-primary/30 rounded-md px-3 py-1.5">
              <ExternalLink className="w-3.5 h-3.5" /> View Safety Data Sheet (SDS)
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default function CoshhRegister() {
  const { user } = useAuth();
  const [substances, setSubstances] = useState<Substance[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    if (!user) return;
    getCoshhData({}).then(d => setSubstances(d.substances)).finally(() => setLoading(false));
  }, [user]);

  const filtered = useMemo(() =>
    substances.filter(s => {
      const matchCat = activeCategory === 'All' || s.category === activeCategory;
      const matchSearch = !search || s.productName.toLowerCase().includes(search.toLowerCase()) || (s.hazardsDescription || '').toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    }),
    [substances, search, activeCategory]
  );

  const highRiskCount = substances.filter(s => s.riskLevel === 'High').length;

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border">
        <div className="px-6 md:px-8 py-6 max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <FlaskConical className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">COSHH Register</h1>
              <p className="text-sm text-muted-foreground">Hazardous substances, PPE requirements &amp; safety data</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-8 py-6 max-w-4xl mx-auto space-y-5">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="border border-border rounded-lg p-3 bg-card">
            <p className="text-2xl font-bold text-foreground">{substances.length}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Total Substances</p>
          </div>
          <div className="border border-red-500/20 rounded-lg p-3 bg-red-500/5">
            <p className="text-2xl font-bold text-red-600">{highRiskCount}</p>
            <p className="text-xs text-muted-foreground mt-0.5">High Risk</p>
          </div>
          <div className="border border-border rounded-lg p-3 bg-card">
            <p className="text-2xl font-bold text-foreground">{[...new Set(substances.map(s => s.category).filter(Boolean))].length}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Categories</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search substances..." className="pl-9" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {['All', ...CATEGORIES].map(cat => (
            <button key={cat} onClick={() => setActiveCategory(cat)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${activeCategory === cat ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-border rounded-xl p-12 text-center text-muted-foreground">
            <FlaskConical className="w-8 h-8 mx-auto mb-3 opacity-30" />
            <p className="text-sm">{search ? `No substances matching "${search}"` : 'No COSHH substances in the register yet.'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(sub => <SubstanceCard key={sub.id} sub={sub} />)}
          </div>
        )}

        <div className="text-xs text-muted-foreground bg-muted/30 border border-border rounded-lg p-3 leading-relaxed">
          <strong className="text-foreground">COSHH Compliance Note: </strong>
          Under the Control of Substances Hazardous to Health Regulations 2002, all staff must be aware of the hazardous substances they may encounter, how to use them safely, and what PPE to wear. If you are unsure about any substance, speak to your manager before use.
        </div>
      </div>
    </div>
  );
}
